import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropzoneRootProps } from 'react-dropzone';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import ImagePreview from '../../../layouts/common/ImagePreview';
import DropZoneIndicator from '../../../layouts/common/DropZoneIndicator';
import AttachmentPreview from '../../../layouts/common/AttachmentPreview';
import AttachmentCaptionEditor from '../../../layouts/common/AttachmentCaptionEditor';
import AttachmentsCounter from '../AttachmentsCounter';
import ReplaceImageButton from '../ReplaceImageButton';
import PreviewThumbs from '../PreviewThumbs';
import UploadingLoader from '../UploadingLoader';
import RemoveAttachmentButton from '../RemoveAttachmentButton';

import s from './Preview.scss';

interface Props {
  deviation: PapiDeviation;
  selectedAttachmentId?: number;
  getSourceFileRootProps: () => DropzoneRootProps;
  getAttachmentRootProps: () => DropzoneRootProps;
  uploadProgress?: number;
  isUploading: boolean;
  isDragging: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
  onAttachmentAdded?: (
    attachment: PapiAttachmentFile,
    deviation: PapiDeviation
  ) => void;
  onRemoveAttachment?: (fileId: number) => void;
  onSelectAttachmentId?: (attachmentId?: number) => void;
}

export const Preview: React.FC<Props> = ({
  deviation,
  selectedAttachmentId,
  getSourceFileRootProps,
  getAttachmentRootProps,
  uploadProgress,
  isUploading,
  isDragging,
  isDragAccept,
  isDragReject,
  onAttachmentAdded: onAttachmentAddedProp,
  onRemoveAttachment: onRemoveAttachmentProp,
  onSelectAttachmentId: onSelectAttachmentIdProp,
}) => {
  const { t } = useTranslation();
  const hasAttachments = useMemo(
    () => (deviation?.extended?.additionalMedia?.length ?? 0) > 0,
    [deviation]
  );

  const selectedAttachment = useMemo(
    () =>
      selectedAttachmentId
        ? deviation?.extended?.additionalMedia?.find(
            a => a.fileId === selectedAttachmentId
          )
        : undefined,
    [deviation, selectedAttachmentId]
  );

  const onSelectAttachment = useCallback(
    (attachment?: PapiAttachmentFile) => {
      onSelectAttachmentIdProp?.(attachment?.fileId);
    },
    [onSelectAttachmentIdProp]
  );

  const onAttachmentAdded = useCallback(
    (attachment: PapiAttachmentFile, updatedDeviation: PapiDeviation) => {
      onAttachmentAddedProp?.(attachment, updatedDeviation);
      onSelectAttachmentIdProp?.(attachment.fileId);
    },
    [onAttachmentAddedProp, onSelectAttachmentIdProp]
  );

  const onRemoveSelectedAttachment = useCallback(() => {
    if (selectedAttachmentId) {
      onRemoveAttachmentProp?.(selectedAttachmentId);
      onSelectAttachmentIdProp?.(undefined);
    }
  }, [onRemoveAttachmentProp, onSelectAttachmentIdProp, selectedAttachmentId]);

  const onRemoveAttachment = useCallback(
    (fileId: number) => {
      onRemoveAttachmentProp?.(fileId);
      if (selectedAttachmentId === fileId) {
        onSelectAttachmentIdProp?.(undefined);
      }
    },
    [onRemoveAttachmentProp, onSelectAttachmentIdProp, selectedAttachmentId]
  );

  return (
    <div className={s['root']}>
      <div className={s['preview-header']}>
        <AttachmentsCounter deviation={deviation} />
        {selectedAttachment ? (
          <RemoveAttachmentButton
            disabled={isUploading}
            onRemoveAttachment={onRemoveSelectedAttachment}
          />
        ) : (
          <ReplaceImageButton
            deviation={deviation}
            disabled={isUploading}
            getDropzoneRootProps={getSourceFileRootProps}
          />
        )}
      </div>
      <div className={s['preview-image-wrapper']} {...getAttachmentRootProps()}>
        <DropZoneIndicator
          isDraggingEnabled={!isUploading}
          isDragActive={isDragging}
          isDragAccept={isDragAccept}
          isDragReject={isDragReject}
        >
          <div className={s['preview-image']}>
            {!selectedAttachment && <ImagePreview deviation={deviation} />}
            {selectedAttachment && (
              <AttachmentPreview attachment={selectedAttachment} />
            )}
          </div>
        </DropZoneIndicator>
        {
          isUploading &&
          getDeviationType(deviation) !== DeviationTypes.ORIGINALS && (
            <UploadingLoader uploadProgress={uploadProgress} />
          )
        }
      </div>
      {
        isUploading &&
        getDeviationType(deviation) === DeviationTypes.ORIGINALS && (
          <UploadingLoader uploadProgress={uploadProgress} />
        )
      }
      <div className={s['preview-footer']}>
        {hasAttachments && (
          <div className={s['attachment-caption-editor']}>
            <AttachmentCaptionEditor
              key={selectedAttachmentId}
              deviation={deviation}
              attachment={selectedAttachment}
              placeholder={t('deviationSubmit.form.imageReplace.caption.label')}
            />
          </div>
        )}
        <PreviewThumbs
          deviation={deviation}
          isUploading={isUploading}
          selectedAttachmentId={selectedAttachmentId}
          onSelectAttachment={onSelectAttachment}
          onAttachmentAdded={onAttachmentAdded}
          onRemoveAttachment={onRemoveAttachment}
        />
      </div>
    </div>
  );
};
Preview.displayName = 'Preview';

export default Preview;
